import { ConcentratedLoad, DistributedLoad } from '@paragon/contract-library';
import { Direction2D, distanceEquals } from '@paragon/geometry-library';
import { assertExhaustiveSwitch, secantOfSlope, slopeFromPoints } from '@paragon/utilities';

export type LoadVisualLoad = Omit<ConcentratedLoad, 'loadCaseGuid'> | Omit<DistributedLoad, 'loadCaseGuid'>;

export type DrawableLoad = { draw: 'Bottom' | 'Top' | 'Projected'; load: LoadVisualLoad };
export const prepareLoadForDrawing =
  (unproject: boolean) =>
  (load: LoadVisualLoad): DrawableLoad => {
    // If the load is not one that can be unprojected or no loads are being shown unprojected, just pass it through unaltered
    if (!unproject || !canLoadBeShownUnprojected(load)) return { draw: 'Projected', load };
    const loadType =
      load.memberType === 'BottomChord'
        ? 'Bottom'
        : load.memberType === 'TopChord'
          ? 'Top'
          : assertExhaustiveSwitch(load.memberType as unknown as never);
    return { draw: loadType, load: unprojectLoad(load) };
  };

export function canLoadBeShownUnprojected(load: LoadVisualLoad): boolean {
  // Non-chord loads should never be displayed unprojected
  if (load.memberType !== 'BottomChord' && load.memberType !== 'TopChord') return false;
  // Manual loads should never be displayed unprojected; they match the user-entered magnitude when projected.
  if (load.isManualLoad) return false;
  // Non-gravity loads should never be displayed unprojected (Unimplemented exception: https://github.com/ParagonTruss/ParagonCore/pull/7763#discussion_r1235808985)
  if (!Direction2D.fromObject(load.direction)?.equals(Direction2D.down)) return false;
  // Loads with a vertical load segment should never be displayed unprojected to avoid arithmetic overflow
  if (load.end && distanceEquals(load.start.x, load.end.x)) return false;
  return true;
}

export function unprojectLoad(load: LoadVisualLoad): LoadVisualLoad {
  switch (load.distribution) {
    case 'Concentrated':
      // Concentrated loads have the same magnitude when they are unprojected
      return { ...load, start: { ...load.start, y: 0 } };
    case 'Distributed':
      // Find unprojection coefficient (secant of the load segment's angle with horizontal) and set load magnitude accordingly
      const secTheta = secantOfSlope(slopeFromPoints(load.start.x, load.start.y, load.end.x, load.end.y));
      const startMagnitude = load.startMagnitude * secTheta;
      const endMagnitude = load.endMagnitude * secTheta;
      return { ...load, startMagnitude, endMagnitude, start: { ...load.start, y: 0 }, end: { ...load.end, y: 0 } };
    default:
      assertExhaustiveSwitch(load);
  }
}
